import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { PreferredSide } from '@sorare/core/src/__generated__/globalTypes';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';

import type { HandInfoChip_BaseballPlayer } from './__generated__/index.graphql';

const messages = {
  pitching: {
    [PreferredSide.BOTH]: defineMessages({
      short: {
        id: 'HandInfoChip.pitching.both.short',
        defaultMessage: 'SHP',
      },
      long: {
        id: 'HandInfoChip.pitching.both.long',
        defaultMessage: 'Switch-Handed Pitcher',
      },
    }),
    [PreferredSide.LEFT]: defineMessages({
      short: {
        id: 'HandInfoChip.pitching.left.short',
        defaultMessage: 'LHP',
      },
      long: {
        id: 'HandInfoChip.pitching.left.long',
        defaultMessage: 'Left-Handed Pitcher',
      },
    }),
    [PreferredSide.RIGHT]: defineMessages({
      short: {
        id: 'HandInfoChip.pitching.right.short',
        defaultMessage: 'RHP',
      },
      long: {
        id: 'HandInfoChip.pitching.right.long',
        defaultMessage: 'Right-Handed Pitcher',
      },
    }),
  },
  batting: {
    [PreferredSide.BOTH]: defineMessages({
      short: {
        id: 'HandInfoChip.batting.both.short',
        defaultMessage: 'S',
      },
      long: {
        id: 'HandInfoChip.batting.both.long',
        defaultMessage: 'Switch-Handed Batter',
      },
    }),
    [PreferredSide.LEFT]: defineMessages({
      short: {
        id: 'HandInfoChip.batting.left.short',
        defaultMessage: 'L',
      },
      long: {
        id: 'HandInfoChip.batting.left.long',
        defaultMessage: 'Left-Handed Batter',
      },
    }),
    [PreferredSide.RIGHT]: defineMessages({
      short: {
        id: 'HandInfoChip.batting.right.short',
        defaultMessage: 'R',
      },
      long: {
        id: 'HandInfoChip.batting.right.long',
        defaultMessage: 'Right-Handed Batter',
      },
    }),
  },
};

type Props = {
  position: 'pitching' | 'batting';
  player: HandInfoChip_BaseballPlayer;
} & Pick<ComponentProps<typeof Chip>, 'size'>;

export const HandInfoChip = ({ position, player, size = 'small' }: Props) => {
  const handInfo = player[position === 'pitching' ? 'throwHand' : 'batHand'];
  if (!handInfo) return null;

  return (
    <Tooltip
      title={<FormattedMessage {...messages[position][handInfo].long} />}
    >
      <Chip size={size}>
        <FormattedMessage {...messages[position][handInfo].short} />
      </Chip>
    </Tooltip>
  );
};

HandInfoChip.fragments = {
  BaseballPlayer: gql`
    fragment HandInfoChip_BaseballPlayer on BaseballPlayer {
      slug
      batHand
      throwHand
    }
  ` as TypedDocumentNode<HandInfoChip_BaseballPlayer>,
};
